/**
 * The payroll system a screen talks to (#187): PowerOffice Go and Tripletex answer the same routes in the
 * same shapes (backend/services/payrollPeople.js), so Koble ansatte og lønnsarter and Lønn og kontrakt
 * serve both. `key` is the field a contract change carries the system's value in.
 */
import { useEffect } from 'react';

/**
 * The welcome's «Koble til» (#191) lands on the system's panel: `/innstillinger/lonn#tripletex`
 * scrolls it into view once its status has loaded, since the panel sits at the foot of Lønn.
 */
export function useLandOn(id: string, ready: boolean) {
  useEffect(() => {
    if (ready && window.location.hash === `#${id}`)
      document.getElementById(id)?.scrollIntoView({ block: 'start' });
  }, [id, ready]);
}

export interface PayrollSystem {
  key: 'poweroffice' | 'tripletex';
  base: string;
  /** the name in a sentence: «Henter fra PowerOffice …» */
  short: string;
}
export const POWEROFFICE: PayrollSystem = {
  key: 'poweroffice',
  base: '/admin/poweroffice',
  short: 'PowerOffice',
};
export const TRIPLETEX: PayrollSystem = { key: 'tripletex', base: '/admin/tripletex', short: 'Tripletex' };

export type SystemKey = 'tripletex' | 'poweroffice' | 'visma';
/** What the column holds: a system, null for «Annet, eller vet ikke», or 'unchosen' for a new company that has not picked. */
export type StoredSystem = SystemKey | 'unchosen' | null;
/**
 * The company's payroll system as the screens should name it: a system only when one was picked. Null for
 * «Annet, eller vet ikke» (#288 review fix 1) and for a company that has not chosen yet (owner 2026-11-04:
 * new companies no longer start on Tripletex), so no screen names Tripletex for a company that never said so.
 */
export const systemOf = (t: { payroll_system?: StoredSystem } | null | undefined): SystemKey | null => {
  const k = t?.payroll_system;
  return k === 'tripletex' || k === 'poweroffice' || k === 'visma' ? k : null;
};
/** Whether the administrator has answered «Hvilket lønnssystem bruker dere?», with a system or «Annet». */
export const chosen = (t: { payroll_system?: StoredSystem } | null | undefined) =>
  t?.payroll_system !== 'unchosen';
